/** Number formatting. Pure; used by UI, describe.ts and tests. */

const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'];

function suffixed(n: number, digits = 3): string {
  const abs = Math.abs(n);
  const tier = Math.min(SUFFIXES.length - 1, Math.floor(Math.log10(abs) / 3));
  if (tier >= SUFFIXES.length - 1 && abs >= 1e36) return n.toExponential(2);
  const scaled = n / Math.pow(1000, tier);
  const decimals = Math.max(0, digits - 1 - Math.floor(Math.log10(Math.abs(scaled))));
  return scaled.toFixed(Math.min(2, decimals)) + SUFFIXES[tier];
}

/** Balances and prices: "$1,234,567" below 10M, then "$12.3M", "$1.23B"… */
export function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return n > 0 ? '$∞' : '$0';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs < 10_000_000) return `${sign}$${Math.floor(abs).toLocaleString('en-US')}`;
  return `${sign}$${suffixed(abs)}`;
}

/** Small or fractional amounts ($/s, per-egg values). */
export function formatRate(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '$0';
  if (n < 10) return `$${n.toFixed(2)}`;
  if (n < 1000) return `$${n.toFixed(1)}`;
  if (n < 1_000_000) return `$${Math.round(n).toLocaleString('en-US')}`;
  return `$${suffixed(n)}`;
}

export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return '∞';
  if (Math.abs(n) < 1_000_000) return Math.floor(n).toLocaleString('en-US');
  return suffixed(n);
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return '∞';
  const total = Math.max(0, Math.round(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Multiplier as a readable phrase: ×2, +25%, −15%. */
export function formatMult(m: number): string {
  if (m >= 2) return `×${Number.isInteger(m) ? m : m.toFixed(2)}`;
  const pct = Math.round((m - 1) * 100);
  return pct >= 0 ? `+${pct}%` : `−${Math.abs(pct)}%`;
}
